/**
 * Autosave service for session data
 * Uses localStorage as backup and Supabase for persistence
 */

const AUTOSAVE_KEY_PREFIX = 'session_autosave_'
const AUTOSAVE_INTERVAL = 30000 // 30 seconds

/**
 * Compare two draft data objects to check if they're identical
 */
const compareDraftData = (data1, data2) => {
  if (!data1 || !data2) return false
  
  // Create normalized copies for comparison
  const normalize = (data) => {
    const normalized = { ...data }
    // Remove metadata fields that don't affect content
    delete normalized.savedAt
    delete normalized.updated_at
    // Deep clone to avoid mutating original
    return JSON.parse(JSON.stringify(normalized))
  }
  
  const normalized1 = normalize(data1)
  const normalized2 = normalize(data2)
  
  // Compare as JSON strings (deep comparison)
  return JSON.stringify(normalized1) === JSON.stringify(normalized2)
}

export const saveToLocalStorage = (sessionId, data) => {
  try {
    const key = `${AUTOSAVE_KEY_PREFIX}${sessionId}`
    
    // Check if we have existing data
    const existingData = loadFromLocalStorage(sessionId)
    
    // If content is identical, just update the timestamp
    if (existingData && compareDraftData(existingData, data)) {
      // Content hasn't changed - just update timestamp to keep it fresh
      localStorage.setItem(key, JSON.stringify({
        ...existingData,
        savedAt: new Date().toISOString(),
      }))
      return true
    }
    
    // Content has changed - save new data
    localStorage.setItem(key, JSON.stringify({
      ...data,
      savedAt: new Date().toISOString(),
    }))
    return true
  } catch (error) {
    console.error('Error saving to localStorage:', error)
    return false
  }
}

export const loadFromLocalStorage = (sessionId) => {
  try {
    const key = `${AUTOSAVE_KEY_PREFIX}${sessionId}`
    const data = localStorage.getItem(key)
    if (data) {
      return JSON.parse(data)
    }
    return null
  } catch (error) {
    console.error('Error loading from localStorage:', error)
    return null
  }
}

export const clearLocalStorage = (sessionId) => {
  try {
    const key = `${AUTOSAVE_KEY_PREFIX}${sessionId}`
    localStorage.removeItem(key)
    return true
  } catch (error) {
    console.error('Error clearing localStorage:', error)
    return false
  }
}

export const getAllAutosavedSessions = () => {
  const sessions = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(AUTOSAVE_KEY_PREFIX)) {
        const data = localStorage.getItem(key)
        if (data) {
          sessions.push({
            id: key.replace(AUTOSAVE_KEY_PREFIX, ''),
            ...JSON.parse(data),
          })
        }
      }
    }
  } catch (error) {
    console.error('Error getting autosaved sessions:', error)
  }
  return sessions
}

export { AUTOSAVE_INTERVAL }

